import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { SituationPage } from "@/components/situation-page";
import { FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "What is a sunset clause?",
    a: "It's the clause in an off-the-plan contract that sets a deadline, called the sunset date. If the developer hasn't registered the plan (or finished the building) by that date, the contract can usually be ended. It protects you from waiting forever for a project that stalls.",
  },
  {
    q: "What happens if the sunset date passes?",
    a: "Usually either party can end the contract and your deposit is refunded. The exact rights depend on the wording of your contract and the law that applies, so get advice before you give or accept any notice.",
  },
  {
    q: "Can the developer extend the sunset date?",
    a: "Some contracts allow extensions for delays such as bad weather or supply problems. Queensland law limits how far a developer can push the date out and how they can use a sunset clause to end a contract. Read the extension clause carefully before you sign.",
  },
  {
    q: "Why would a developer want to end the contract?",
    a: "If prices have risen since you signed, a developer could, in theory, benefit from ending your contract and reselling for more. That's why the law restricts this, and why you should have the sunset clause checked before signing.",
  },
  {
    q: "What should I check before signing an off-the-plan contract?",
    a: "The sunset date and any extension rights, how long your deposit is held and where, what can change in the final plan, the body corporate budget, and when you have to settle after registration. We review these as part of our free contract review.",
  },
];

export const Route = createFileRoute("/sunset-date-off-the-plan-qld")({
  head: () => ({
    meta: [
      { title: fitTitle("Sunset Clause QLD: What It Means for Buyers | Coastside Law") },
      {
        name: "description",
        content: fitDescription(
          "Sunset clauses in Queensland off-the-plan contracts explained: what a sunset date is, what happens if the date passes, extensions and what to check before you sign.",
        ),
      },
      { property: "og:title", content: "Sunset clauses in off-the-plan contracts, explained" },
      {
        property: "og:description",
        content: fitDescription("What a sunset clause is and why it matters when buying off the plan in Queensland."),
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "/sunset-date-off-the-plan-qld" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/sunset-date-off-the-plan-qld" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: () => (
    <SituationPage
      eyebrow="Buying off the plan"
      title="What is a sunset clause?"
      lead="A sunset clause sets the deadline (the sunset date) for the developer to deliver. Here's what it means for your off-the-plan purchase in Queensland and what to check before you sign."
      boxTitle="The short version"
      boxItems={[
        "The deadline for the plan to be registered or the building finished",
        "If it passes, the contract can usually be ended and the deposit refunded",
        "Extension clauses can push the date out, so read them",
        "Queensland law limits how developers can use sunset clauses",
        "Get advice before giving or accepting any termination notice",
      ]}
      faqs={FAQS}
      links={[
        { to: "/off-the-plan-contracts", label: "Off-the-plan contracts" },
        { to: "/buyers-solicitor-qld", label: "Buyer's solicitor" },
        { to: "/special-conditions-contract-qld", label: "Special conditions explained" },
      ]}
      formHeading="Send us your off-the-plan contract"
      ctaTitle="Buying off the plan?"
      ctaBody={`Call ${FIRM.phone}. We'll check the sunset date and extension clauses before you sign.`}
    />
  ),
});
